/**
 * src/pages/ReportsPage.tsx
 * Lists reports from API. Uses modal for confirmations.
 */

import { useState, useEffect, useCallback, useMemo, memo } from "react";
import type { ReportInstance } from "../report-engine/types";
import { getTemplate } from "../report-engine/templates";
import { listReports, getReport, deleteReport, type ReportSummary } from "../api/reportApi";
import ReportLogo from "../components/ReportLogo";
import { BtnOpen, BtnDelete } from "../components/IconButtons";
import ConfirmModal, { type ConfirmModalProps } from "../components/ConfirmModal";
import { useSiteCache } from "../utils/siteCache";

type Variant  = "mensuel"|"trimestriel"|"annuel";
type SortKey  = "period" | "updated" | "client" | "template";
type SortDir  = "asc" | "desc";

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "period",   label: "Période"      },
  { key: "updated",  label: "Modification" },
  { key: "client",   label: "Client"       },
  { key: "template", label: "Type"         },
];

/** Clé de tri chronologique normalisée pour les period keys :
 *  "2024"     → "2024-13"  (annuel, après décembre)
 *  "2024-T3"  → "2024-09"  (fin du trimestre)
 *  "2024-07"  → "2024-07"  (inchangé)
 */
function toPeriodSortKey(pk: string): string {
  if (/^\d{4}$/.test(pk)) return `${pk}-13`;
  const t = pk.match(/^(\d{4})-T(\d)$/);
  if (t) return `${t[1]}-${String(Number(t[2]) * 3).padStart(2, "0")}`;
  return pk;
}

/** "mensuel-multi" est une variante de "mensuel" (plusieurs sites, même client) —
 * le filtre par type doit donc l'inclure quand on filtre sur "mensuel". */
function matchesTemplateFilter(templateId: string, filterType: string): boolean {
  if (!filterType) return true;
  if (filterType === "mensuel") return templateId === "mensuel" || templateId === "mensuel-multi";
  return templateId === filterType;
}

/** Titre affiché pour un rapport — "Client — Site". Pour un rapport multi-sites,
 * `siteId` est synthétique et ne correspond à aucun vrai site (cache miss côté
 * `getSiteName`) : on retombe alors sur le `siteName` stocké sur le rapport. */
function reportTitle(
  s: ReportSummary, cacheReady: boolean,
  getClientName: (id: string) => string, getSiteName: (id: string) => string,
): string {
  if (!cacheReady) return s.siteId;
  const clientPart = getClientName(s.siteId);
  const sitePart    = getSiteName(s.siteId);
  return (sitePart === s.siteId && s.siteName)
    ? [clientPart, s.siteName].filter(Boolean).join(" — ")
    : [clientPart, sitePart].filter(Boolean).join(" — ");
}

// Formateur de date partagé — évite de recréer une instance Intl à chaque rendu
const dtf = new Intl.DateTimeFormat("fr-FR", {
  day:"numeric", month:"long", year:"numeric", hour:"2-digit", minute:"2-digit",
});
function fmt(iso: string) { return dtf.format(new Date(iso)); }

// Préférence d'affichage (compact / normal) — persistée entre sessions.
const COMPACT_STORAGE_KEY = "reportsPage.compact";

function logoPropsFromPeriodKey(periodKey: string | null | undefined): { variant: Variant; sliceIndex: number } {
  if (!periodKey) return { variant: "mensuel", sliceIndex: 1 };
  if (/^\d{4}$/.test(periodKey))         return { variant: "annuel",      sliceIndex: 1 };
  const tMatch = periodKey.match(/^\d{4}-T(\d)$/);
  if (tMatch)                             return { variant: "trimestriel", sliceIndex: Number(tMatch[1]) };
  const mMatch = periodKey.match(/^\d{4}-(\d{2})$/);
  if (mMatch)                             return { variant: "mensuel",     sliceIndex: Number(mMatch[1]) };
  return { variant: "mensuel", sliceIndex: 1 };
}

export function PageIcon({ periodKey, size = 46 }: { periodKey?: string; size?: number }) {
  const { variant, sliceIndex } = periodKey
    ? logoPropsFromPeriodKey(periodKey)
    : { variant: "mensuel" as Variant, sliceIndex: 1 };
  const logoSize = Math.round(size * 0.48);

  return (
    <div style={{ width: size, height: size, position: "relative", flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 46 46" fill="none"
        xmlns="http://www.w3.org/2000/svg" style={{ position: "absolute", inset: 0 }}>
        <rect x="4" y="2" width="28" height="36" rx="2" fill="#f0f0f0" stroke="#d0d0d0" strokeWidth="1.5"/>
        <polygon points="25,2 32,2 32,9" fill="#d0d0d0"/>
        <polygon points="25,2 32,9 25,9" fill="#e0e0e0"/>
      </svg>
      {periodKey && (
        <div style={{
          position: "absolute",
          left: Math.round(18 / 46 * size),
          top:  Math.round(20 / 46 * size),
          transform: "translate(-50%, -50%)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
        }}>
          <ReportLogo variant={variant} sliceIndex={sliceIndex} size={logoSize}/>
          <div style={{ width: Math.round(logoSize * 0.85), height: 3, borderRadius: 2, background: "#c8c8c8" }}/>
        </div>
      )}
    </div>
  );
}

export function EmptyPageIcon({ size = 72 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="4" width="44" height="56" rx="3" fill="#f0f0f0" stroke="#d0d0d0" strokeWidth="2"/>
      <polygon points="40,4 52,4 52,16" fill="#d0d0d0"/>
      <polygon points="40,4 52,16 40,16" fill="#e8e8e8"/>
      <rect x="16" y="26" width="28" height="2.5" rx="1.2" fill="#ddd"/>
      <rect x="16" y="32" width="20" height="2.5" rx="1.2" fill="#e8e8e8"/>
      <rect x="16" y="38" width="24" height="2.5" rx="1.2" fill="#e8e8e8"/>
    </svg>
  );
}

// ── Carte rapport mémoïsée ─────────────────────────────────────────────────────
// Ne re-rend que si ses propres props changent — évite de tout re-rendre quand
// `opening` passe à l'id d'une autre carte.

interface ReportCardProps {
  s:              ReportSummary;
  cacheReady:     boolean;
  getClientName:  (siteId: string) => string;
  getSiteName:    (siteId: string) => string;
  onOpen:         (s: ReportSummary) => void;
  onDelete:       (s: ReportSummary) => void;
  isOpening:      boolean;
  readOnly:       boolean;
  compact:        boolean;
}

const ReportCard = memo(function ReportCard({
  s, cacheReady, getClientName, getSiteName, onOpen, onDelete, isOpening, readOnly, compact,
}: ReportCardProps) {
  const tpl   = getTemplate(s.templateId);
  const title = reportTitle(s, cacheReady, getClientName, getSiteName);
  const tooltip = [
    s.createdBy ? `Créé le ${fmt(s.createdAt)} par ${s.createdBy}` : `Créé le ${fmt(s.createdAt)}`,
    s.updatedBy ? `Modifié le ${fmt(s.updatedAt)} par ${s.updatedBy}` : `Modifié le ${fmt(s.updatedAt)}`,
  ].join("\n");

  return (
    <div className="card flex gap-16" style={{ padding: compact ? "8px 14px" : "18px 20px",
      alignItems:"center", gap: compact ? 10 : 16 }}>
      <PageIcon periodKey={s.periodKey} size={compact ? 26 : 46}/>
      <div className="flex-1 overflow-h" title={tooltip} style={{ cursor:"default" }}>
        {compact ? (
          <div className="flex gap-8" style={{ alignItems:"baseline" }}>
            <span className="t-title" style={{ fontSize:13, color:"var(--text-primary)",
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {title}
            </span>
            <span style={{ color:"#ddd", fontSize:11, flexShrink:0 }}>·</span>
            <span style={{ fontSize:11, color:"var(--text-muted)", flexShrink:0 }}>{tpl?.label ?? s.templateId}</span>
            <span style={{ color:"#ddd", fontSize:11, flexShrink:0 }}>·</span>
            <span style={{ fontSize:11, color:"var(--text-muted)", flexShrink:0 }}>{s.periodKey}</span>
          </div>
        ) : (
          <>
            <div className="t-title" style={{ fontSize:15, color:"var(--text-primary)", marginBottom:3,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {title}
            </div>
            <div className="flex gap-10" style={{ fontSize:12, color:"var(--text-muted)" }}>
              <span>{tpl?.label ?? s.templateId}</span>
              <span style={{ color:"#ddd" }}>·</span>
              <span>{s.periodKey}</span>
            </div>
          </>
        )}
      </div>
      <div className="flex gap-8 shrink-0">
        <BtnOpen onClick={() => onOpen(s)} title="Ouvrir le rapport" disabled={isOpening}/>
        {!readOnly && <BtnDelete onClick={() => onDelete(s)} title="Supprimer le rapport" disabled={isOpening}/>}
      </div>
    </div>
  );
});

// ── Page principale ────────────────────────────────────────────────────────────

interface Props {
  onOpen:    (i: ReportInstance) => void;
  onCreate:  () => void;
  readOnly?: boolean;
}

export default function ReportsPage({ onOpen, onCreate, readOnly = false }: Props) {
  const { ready: cacheReady, getSiteName, getClientName } = useSiteCache();
  const [summaries,  setSummaries]  = useState<ReportSummary[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [opening,    setOpening]    = useState<string | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [modal,        setModal]        = useState<ConfirmModalProps | null>(null);
  const [compact,      setCompact]      = useState(() => localStorage.getItem(COMPACT_STORAGE_KEY) === "1");
  const [query,        setQuery]        = useState("");
  const [sortKey,      setSortKey]      = useState<SortKey>("period");
  const [sortDir,      setSortDir]      = useState<SortDir>("desc");
  const [filterType,   setFilterType]   = useState("");   // "" = tous
  const [filterClient, setFilterClient] = useState("");   // "" = tous
  const [filterSite,   setFilterSite]   = useState("");   // "" = tous
  const [filterYear,   setFilterYear]   = useState("");   // "" = toutes

  const toggleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "client" || key === "template" ? "asc" : "desc");
    }
  }, [sortKey]);

  // Quand le client change, réinitialiser le filtre site
  const handleFilterClient = useCallback((v: string) => {
    setFilterClient(v);
    setFilterSite("");
  }, []);

  const clearAllFilters = useCallback(() => {
    setQuery(""); setFilterType(""); setFilterClient(""); setFilterSite(""); setFilterYear("");
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setSummaries(await listReports());
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // Options de filtres dérivées des rapports chargés
  const filterOptions = useMemo(() => {
    const yearSet    = new Set<string>();
    const clientSet  = new Set<string>();
    for (const s of summaries) {
      yearSet.add(s.periodKey.slice(0, 4));
      if (cacheReady) clientSet.add(getClientName(s.siteId));
    }
    const years   = [...yearSet].sort().reverse();
    const clients = [...clientSet].sort((a, b) => a.localeCompare(b, "fr"));

    // Sites filtrés par le client sélectionné (évite d'afficher des sites hors contexte)
    const siteMap = new Map<string, string>();
    for (const s of summaries) {
      if (filterClient && cacheReady && getClientName(s.siteId) !== filterClient) continue;
      siteMap.set(s.siteId, cacheReady ? getSiteName(s.siteId) : s.siteId);
    }
    const sites = [...siteMap.entries()].sort((a, b) => a[1].localeCompare(b[1], "fr"));

    return { years, clients, sites };
  }, [summaries, cacheReady, getClientName, getSiteName, filterClient]);

  const hasFilters = !!(filterType || filterClient || filterSite || filterYear);
  const isFiltered = !!(query || hasFilters);

  // Filtrage + tri côté client
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const result = summaries.filter(s => {
      // Recherche textuelle
      if (q) {
        const title = reportTitle(s, cacheReady, getClientName, getSiteName).toLowerCase();
        if (!title.includes(q) && !s.periodKey.toLowerCase().includes(q) && !s.templateId.toLowerCase().includes(q))
          return false;
      }
      // Filtres
      if (!matchesTemplateFilter(s.templateId, filterType)) return false;
      if (filterYear   && !s.periodKey.startsWith(filterYear)) return false;
      if (filterClient && cacheReady && getClientName(s.siteId) !== filterClient) return false;
      if (filterSite   && s.siteId !== filterSite) return false;
      return true;
    });

    const dir = sortDir === "asc" ? 1 : -1;
    return [...result].sort((a, b) => {
      switch (sortKey) {
        case "period":
          return dir * toPeriodSortKey(a.periodKey).localeCompare(toPeriodSortKey(b.periodKey));
        case "updated":
          return dir * a.updatedAt.localeCompare(b.updatedAt);
        case "client": {
          const ca = reportTitle(a, cacheReady, getClientName, getSiteName);
          const cb = reportTitle(b, cacheReady, getClientName, getSiteName);
          return dir * ca.localeCompare(cb, "fr");
        }
        case "template":
          return dir * a.templateId.localeCompare(b.templateId);
        default:
          return 0;
      }
    });
  }, [summaries, query, sortKey, sortDir,
      filterType, filterYear, filterClient, filterSite,
      cacheReady, getClientName, getSiteName]);

  const handleOpen = useCallback(async (s: ReportSummary) => {
    setOpening(s.id);
    try {
      const full = await getReport(s.id);
      // For old reports that pre-date the siteName field, enrich from live site cache
      if (!full.siteName && cacheReady) full.siteName = getSiteName(s.siteId);
      onOpen(full);
    } catch (e: unknown) {
      setError(`Impossible d'ouvrir le rapport : ${(e as Error).message}`);
    } finally {
      setOpening(null);
    }
  }, [onOpen, cacheReady, getSiteName]);

  const handleDelete = useCallback((s: ReportSummary) => {
    const label = reportTitle(s, cacheReady, getClientName, getSiteName);
    setModal({
      title:        "Supprimer ce rapport ?",
      message:      `Le rapport "${label}" (${s.periodKey}) sera définitivement supprimé de la base de données.`,
      confirmLabel: "Supprimer",
      cancelLabel:  "Annuler",
      danger:       true,
      onConfirm: async () => {
        try {
          await deleteReport(s.id);
          setSummaries(prev => prev.filter(i => i.id !== s.id));
        } catch (e: unknown) {
          setError(`Suppression échouée : ${(e as Error).message}`);
        }
      },
      onCancel: () => setModal(null),
    });
  }, [cacheReady, getClientName, getSiteName]);

  const countLabel = loading ? "Chargement…"
    : error   ? "Erreur de chargement"
    : summaries.length === 0 ? "Aucun rapport."
    : isFiltered && filtered.length !== summaries.length
      ? `${filtered.length} / ${summaries.length} rapport${summaries.length > 1 ? "s" : ""}`
      : `${summaries.length} rapport${summaries.length > 1 ? "s" : ""}`;

  return (
    <div className="screen" style={{ height:"100%", overflow:"hidden", paddingBottom:0 }}>
      <div className="screen-inner" style={{ height:"100%", display:"flex", flexDirection:"column" }}>

        {/* ── En-tête fixe ── */}
        <div style={{ flexShrink:0 }}>
        <div className="flex-between mb-16">
          <div>
            <h1 className="page-title">Rapports</h1>
            <p className="page-subtitle">{countLabel}</p>
          </div>
          <div className="flex gap-8" style={{ alignItems:"center" }}>
            <button onClick={() => setCompact(c => { const next = !c; localStorage.setItem(COMPACT_STORAGE_KEY, next ? "1" : "0"); return next; })} className="btn btn-ghost"
              title={compact ? "Affichage détaillé" : "Affichage compact"}
              style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"9px 14px",
                ...(compact ? { background:"var(--green-dim)", color:"#fff", borderColor:"var(--green-dim)" } : {}) }}>
              <span style={{ fontSize:18, lineHeight:1 }}>{compact ? "☰" : "▤"}</span>
            </button>
            <button onClick={loadList} className="btn btn-ghost"
              title="Rafraîchir" style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"9px 14px" }}>
              <span style={{ fontSize:18, lineHeight:1 }}>↻</span>
            </button>
            {!readOnly && <button onClick={onCreate} className="btn btn-black">＋ Créer un rapport</button>}
          </div>
        </div>

        {/* Barre de recherche + tri */}
        {!loading && !error && summaries.length > 0 && (
          <div style={{ marginBottom:20, display:"flex", flexDirection:"column", gap:8 }}>
            {/* Recherche */}
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)",
                color:"var(--text-faint)", fontSize:14, pointerEvents:"none" }}>🔍</span>
              <input
                type="search"
                placeholder="Rechercher par client, site ou période…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="field"
                style={{ paddingLeft:36 }}
              />
            </div>
            {/* Tri */}
            <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, color:"var(--text-faint)", fontFamily:"var(--ff-title)",
                fontWeight:700, letterSpacing:0.5, textTransform:"uppercase", flexShrink:0, minWidth:44 }}>
                Trier
              </span>
              {SORT_OPTIONS.map(({ key, label }) => {
                const active = sortKey === key;
                const arrow  = sortDir === "asc" ? " ↑" : " ↓";
                return (
                  <button key={key} onClick={() => toggleSort(key)}
                    style={{
                      fontSize:11, padding:"3px 10px", borderRadius:20,
                      border: active ? "1.5px solid var(--green-dim)" : "1.5px solid #ddd",
                      background: active ? "var(--green-dim)" : "transparent",
                      color:      active ? "#fff"               : "var(--text-muted)",
                      cursor:"pointer", fontFamily:"var(--ff-title)", fontWeight:active ? 700 : 500,
                      transition:"all 0.15s", whiteSpace:"nowrap",
                    }}>
                    {label}{active ? arrow : ""}
                  </button>
                );
              })}
            </div>

            {/* Filtres */}
            <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, color:"var(--text-faint)", fontFamily:"var(--ff-title)",
                fontWeight:700, letterSpacing:0.5, textTransform:"uppercase", flexShrink:0, minWidth:44 }}>
                Filtrer
              </span>

              {/* Type — chips */}
              {(["", "mensuel", "trimestriel", "annuel"] as const).map(t => {
                const label   = t === "" ? "Tous" : t.charAt(0).toUpperCase() + t.slice(1);
                const active  = filterType === t;
                return (
                  <button key={t} onClick={() => setFilterType(t)}
                    style={{
                      fontSize:11, padding:"3px 10px", borderRadius:20,
                      border: active ? "1.5px solid var(--green-dim)" : "1.5px solid #ddd",
                      background: active ? "var(--green-dim)" : "transparent",
                      color:      active ? "#fff"               : "var(--text-muted)",
                      cursor:"pointer", fontFamily:"var(--ff-title)", fontWeight:active ? 700 : 500,
                      transition:"all 0.15s",
                    }}>
                    {label}
                  </button>
                );
              })}

              {/* Séparateur */}
              <span style={{ color:"#ddd" }}>|</span>

              {/* Année — select */}
              {filterOptions.years.length > 1 && (
                <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
                  className="field"
                  style={{ marginBottom:0, padding:"3px 28px 3px 10px", fontSize:11,
                    height:26, borderRadius:20, minWidth:0, width:"auto",
                    fontFamily:"var(--ff-title)" }}>
                  <option value="">Toutes les années</option>
                  {filterOptions.years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              )}

              {/* Client — select */}
              {filterOptions.clients.length > 1 && (
                <select value={filterClient} onChange={e => handleFilterClient(e.target.value)}
                  className="field"
                  style={{ marginBottom:0, padding:"3px 28px 3px 10px", fontSize:11,
                    height:26, borderRadius:20, minWidth:0, width:"auto",
                    fontFamily:"var(--ff-title)" }}>
                  <option value="">Tous les clients</option>
                  {filterOptions.clients.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}

              {/* Site — select (dépend du client sélectionné) */}
              {filterOptions.sites.length > 1 && (
                <select value={filterSite} onChange={e => setFilterSite(e.target.value)}
                  className="field"
                  style={{ marginBottom:0, padding:"3px 28px 3px 10px", fontSize:11,
                    height:26, borderRadius:20, minWidth:0, width:"auto",
                    fontFamily:"var(--ff-title)" }}>
                  <option value="">Tous les sites</option>
                  {filterOptions.sites.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
              )}

              {/* Effacer tous les filtres */}
              {(isFiltered) && (
                <button onClick={clearAllFilters}
                  style={{
                    fontSize:11, padding:"3px 10px", borderRadius:20,
                    border:"1.5px solid var(--red)", background:"transparent",
                    color:"var(--red)", cursor:"pointer", fontFamily:"var(--ff-title)",
                    fontWeight:600, transition:"all 0.15s", marginLeft:4,
                  }}>
                  ✕ Réinitialiser les filtres
                </button>
              )}
            </div>
          </div>
        )}

        </div>{/* fin en-tête fixe */}

        {/* ── Contenu scrollable ── */}
        <div style={{ flex:1, minHeight:0, overflowY:"auto", paddingBottom:24 }}>

        {/* Erreur */}
        {error && (
          <div className="toast toast-err mb-20" style={{ borderRadius:"var(--r-md)" }}>
            <span>⚠ {error}</span>
            <button onClick={loadList} style={{ marginLeft:"auto", background:"none", border:"none",
              cursor:"pointer", color:"var(--red)", textDecoration:"underline",
              fontFamily:"inherit", fontSize:"inherit" }}>
              Réessayer
            </button>
          </div>
        )}

        {/* Skeleton de chargement */}
        {loading && (
          <div className="flex-col gap-12">
            {[1,2,3].map(i => (
              <div key={i} className="card flex gap-16" style={{ padding:"18px 20px", alignItems:"center" }}>
                <div className="skeleton" style={{ width:46, height:46, borderRadius:6 }}/>
                <div className="flex-1">
                  <div className="skeleton" style={{ height:14, width:"40%", marginBottom:8 }}/>
                  <div className="skeleton" style={{ height:10, width:"60%" }}/>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* État vide — aucun rapport en base */}
        {!loading && !error && summaries.length === 0 && (
          <div className="card p-32" style={{ textAlign:"center" }}>
            <div style={{ margin:"0 auto 16px", display:"inline-block" }}>
              <EmptyPageIcon size={80}/>
            </div>
            <h2 className="t-title" style={{ fontSize:18, color:"var(--text-primary)", marginBottom:8 }}>
              Aucun rapport
            </h2>
            <p style={{ fontSize:13, color:"var(--text-muted)", maxWidth:360, margin:"0 auto 24px" }}>
              Créez votre premier rapport en choisissant un site, un template et une période.
            </p>
            {!readOnly && <button onClick={onCreate} className="btn btn-black">＋ Créer un rapport</button>}
          </div>
        )}

        {/* État vide — aucun résultat de recherche / filtre */}
        {!loading && !error && summaries.length > 0 && filtered.length === 0 && (
          <div className="card p-32" style={{ textAlign:"center", color:"var(--text-faint)" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>🔎</div>
            <p style={{ fontSize:14, marginBottom:12 }}>
              {query && !hasFilters
                ? <>Aucun rapport ne correspond à « <strong>{query}</strong> ».</>
                : hasFilters && !query
                  ? "Aucun rapport ne correspond aux filtres sélectionnés."
                  : "Aucun rapport ne correspond à la recherche et aux filtres sélectionnés."}
            </p>
            <button onClick={clearAllFilters} className="btn btn-ghost btn-sm">
              Effacer la recherche
            </button>
          </div>
        )}

        {/* Liste des rapports */}
        {!loading && !error && filtered.length > 0 && (
          <div className="flex-col" style={{ gap: compact ? 6 : 12 }}>
            {filtered.map(s => (
              <ReportCard
                key={s.id}
                s={s}
                cacheReady={cacheReady}
                getClientName={getClientName}
                getSiteName={getSiteName}
                onOpen={handleOpen}
                onDelete={handleDelete}
                isOpening={opening === s.id}
                readOnly={readOnly}
                compact={compact}
              />
            ))}
          </div>
        )}

        </div>{/* fin contenu scrollable */}

      </div>

      {modal && <ConfirmModal {...modal}/>}
    </div>
  );
}
